// WHAT: Productivity-Widget für einen kompakten Überblick über TimeTracQ-Tasks (Total/Open/Done)
// WHY:  Verknüpft das Productivity-Domain-Feature TimeTracQ mit dem Core-Dashboard und macht Task-Last schnell sichtbar
// DOMAIN: core → dashboard → widgets → productivity
// DEPENDENCIES: `useSupabaseTasks`-Hook (TimeTracQ), `WidgetContainer`, TracQ Grid/Card/Heading/Text
// RISKS:
//   - NOTE: Filterlogik (`status === 'done'`) hängt direkt von den Status-Strings im TimeTracQ-Domainmodell ab

import { CheckSquare, Clock, CheckCircle2 } from "lucide-react";
import { type FC } from "react";

import { Grid, Text, Heading, Card } from "@/components/ui";
import { useSupabaseTasks } from "@/domains/productivity/timetracq/hooks/data/useSupabasetasks";

import { useWidgetConfig } from "../../../hooks/useWidgetConfig";
import { WIDGET_IDS } from "../../../store/widgetRegistry";
import { WidgetContainer } from "../base";

/**
 * WHAT:
 *   Aggregiert die Anzahl aller TimeTracQ-Tasks sowie deren Aufteilung in offene und erledigte Tasks
 *   und zeigt diese in drei Kennzahlenkarten an.
 *
 * WHY:
 *   Dient als schneller Einstiegspunkt ins Task-Management, ohne in die Detail-Ansicht von TimeTracQ
 *   springen zu müssen.
 *
 * BEHAVIOR:
 *   - Lädt Tasks über `useSupabaseTasks` aus der Productivity-Domain.
 *   - Berechnet simple Zählwerte (total, open, done) anhand des `status`-Feldes.
 *
 * RISKS:
 *   - NOTE: Zusätzliche Statuswerte (z. B. „archived") müssten in der Open/Done-Logik berücksichtigt werden.
 *
 * @returns {JSX.Element} TimeTracQ task widget component
 *
 */
export const TimeTracQTaskWidget: FC = () => {
  const { config } = useWidgetConfig(WIDGET_IDS.TIMETRACQ_TASKS);
  const { loading, tasks } = useSupabaseTasks();

  const totalTasks = tasks.length;
  const openTasks = tasks.filter((t) => t.status !== "done").length;
  const doneTasks = tasks.filter((t) => t.status === "done").length;

  return (
    <WidgetContainer
      description="Task overview"
      icon={<CheckSquare className="w-5 h-5" />}
      loading={loading}
      size={config?.size ?? "medium"}
      title="TimeTracQ Tasks"
      widgetId={WIDGET_IDS.TIMETRACQ_TASKS}
    >
      <Grid cols={{ lg: 3, md: 3, sm: 3 }} gap={2}>
        <Card className="text-center" padding="sm" variant="default">
          <div className="flex items-center justify-center gap-2 mb-2">
            <CheckSquare className="w-4 h-4 text-gray-400" />
            <Heading className="text-xs text-gray-400" level={5}>
              Total
            </Heading>
          </div>
          <Text className="text-2xl font-bold text-white">{totalTasks}</Text>
        </Card>

        <Card className="text-center" padding="sm" variant="default">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-yellow-400" />
            <Heading className="text-xs text-gray-400" level={5}>
              Open
            </Heading>
          </div>
          <Text className="text-2xl font-bold text-white">{openTasks}</Text>
        </Card>

        <Card className="text-center" padding="sm" variant="default">
          <div className="flex items-center justify-center gap-2 mb-2">
            <CheckCircle2 className="w-4 h-4 text-green-400" />
            <Heading className="text-xs text-gray-400" level={5}>
              Done
            </Heading>
          </div>
          <Text className="text-2xl font-bold text-white">{doneTasks}</Text>
        </Card>
      </Grid>
    </WidgetContainer>
  );
};
